import { useEffect, useRef, useState } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { Autocomplete, Box, Text, TextField } from '@/ui/primitives';
import { buildFilterAutocompletePaperSx } from './filterControlStyles';
import { filterTokens } from './filterTokens';
import {
  buildSelectFilterControlFrameSx,
  buildSelectFilterLabelSx,
  buildSelectFilterLabelWidthSx,
  buildSelectFilterOptionCountSx,
  buildSelectFilterOptionLabelSx,
  buildSelectFilterOptionRowSx,
  buildSelectFilterPopperModifiers,
  buildSelectFilterPopperSx,
  buildSelectFilterRootSx,
  buildSingleSelectFilterAutocompleteSx,
  buildSingleSelectFilterListboxSx,
  selectFilterDefaultControlWidthPx,
  selectFilterDefaultFontSizePx,
  selectFilterReadOnlyInputStyle,
} from './selectFilterControlStyles';
import type { SvgIconComponent } from '@/ui/primitives';
import type { FormFieldBackgroundColor } from '@/ui/primitives/formFieldVariants';
import type { ReactElement, ReactNode } from 'react';

type SingleSelectOption = {
  value: string;
  label: string;
  count?: number;
  countIcon?: SvgIconComponent;
  countIconColor?: string;
  countIconSizePx?: number;
};

type SingleSelectFilterFieldProps = {
  value: string;
  defaultValue?: string;
  options: SingleSelectOption[];
  label?: string;
  labelPlacement?: 'stacked';
  labelTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  helperText?: ReactNode;
  fontSizePx?: number;
  labelWidthPx?: number;
  labelControlGap?: string;
  inputAriaLabel?: string;
  emptyLabel?: string;
  controlWidthPx?: number;
  fullWidth?: boolean;
  disabled?: boolean;
  inputHeight?: number;
  error?: boolean;
  required?: boolean;
  inputBackgroundColor?: FormFieldBackgroundColor;
  inputLeftPadding?: string;
  onChange: (next: string) => void;
};

export const SingleSelectFilterField = ({
  value,
  defaultValue,
  options,
  label,
  labelPlacement: _labelPlacement,
  labelTransform: _labelTransform,
  helperText,
  fontSizePx = selectFilterDefaultFontSizePx,
  labelWidthPx: _labelWidthPx,
  labelControlGap = s('sm'),
  inputAriaLabel,
  emptyLabel,
  controlWidthPx = selectFilterDefaultControlWidthPx,
  fullWidth = false,
  disabled = false,
  inputHeight,
  error = false,
  required = false,
  inputBackgroundColor = 'var(--control-surface)',
  inputLeftPadding = s('sm'),
  onChange,
}: SingleSelectFilterFieldProps): ReactElement => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const hasActiveValue = defaultValue !== undefined && value !== defaultValue;
  const resolvedControlWidth = `${controlWidthPx}px`;
  const controlHeight = inputHeight ?? componentSizes.filters.controlHeight;
  const hasLabel = Boolean(label);
  const [measuredControlWidth, setMeasuredControlWidth] = useState(controlWidthPx);
  const optionRowMinHeight = `${filterTokens.optionRowMinHeight}px`;
  const countFontSizePx = Math.max(11, Math.min(fontSizePx, filterTokens.optionCountFontSize));
  const labelPlacement = 'stacked';

  useEffect(() => {
    const element = controlRef.current;
    if (!element) {
      return;
    }

    const updateWidth = (): void => {
      const nextWidth = element.getBoundingClientRect().width;
      if (nextWidth > 0) {
        setMeasuredControlWidth(nextWidth);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const observer = new ResizeObserver(() => {
      updateWidth();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [controlWidthPx, fullWidth]);

  const dropdownWidth = fullWidth ? `${measuredControlWidth}px` : resolvedControlWidth;

  return (
    <Box sx={buildSelectFilterRootSx({ fullWidth, hasLabel, labelControlGap, labelPlacement })}>
      {hasLabel ? (
        <Text
          role="body"
          color="textPrimary"
          transform="none"
          sx={buildSelectFilterLabelSx(fontSizePx, labelPlacement)}
        >
          <Box component="span" sx={buildSelectFilterLabelWidthSx(undefined)}>
            {label}
          </Box>
        </Text>
      ) : null}
      <Box
        ref={controlRef}
        sx={buildSelectFilterControlFrameSx({ fullWidth, labelPlacement, resolvedControlWidth })}
      >
        <Autocomplete
          disableClearable
          selectOnFocus={false}
          disabled={disabled}
          options={options}
          value={selectedOption}
          isOptionEqualToValue={(option, current) => option.value === current.value}
          getOptionLabel={(option) => option.label}
          onChange={(_event, nextOption) => {
            onChange(nextOption.value);
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            const CountIcon = option.countIcon;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={buildSelectFilterOptionRowSx({ optionRowMinHeight })}
              >
                {CountIcon ? (
                  <CountIcon
                    sx={{
                      color: option.countIconColor,
                      flexShrink: 0,
                      fontSize: option.countIconSizePx,
                      height: option.countIconSizePx,
                      width: option.countIconSizePx,
                    }}
                  />
                ) : null}
                <Text
                  role="bodyMuted"
                  color="textPrimary"
                  component="span"
                  sx={buildSelectFilterOptionLabelSx(fontSizePx)}
                >
                  {option.label}
                </Text>
                {option.count !== undefined ? (
                  <Text
                    role="bodyMuted"
                    color="textSecondary"
                    component="span"
                    sx={buildSelectFilterOptionCountSx(countFontSizePx)}
                  >
                    {option.count.toLocaleString()}
                  </Text>
                ) : null}
              </Box>
            );
          }}
          renderInput={(params) => {
            const SelectedIcon = selectedOption?.countIcon;
            return (
              <TextField
                {...params}
                size="small"
                fieldStyle="filter"
                inputHeight={controlHeight}
                placeholder={emptyLabel}
                helperText={helperText}
                error={error}
                required={required}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: SelectedIcon ? (
                      <SelectedIcon
                        sx={{
                          color: selectedOption.countIconColor,
                          flexShrink: 0,
                          fontSize: selectedOption.countIconSizePx,
                          height: selectedOption.countIconSizePx,
                          ml: s('xs'),
                          width: selectedOption.countIconSizePx,
                        }}
                      />
                    ) : (
                      params.InputProps.startAdornment
                    ),
                  },
                }}
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
                inputProps={{
                  ...params.inputProps,
                  'aria-label': inputAriaLabel ?? label ?? 'Select option',
                  readOnly: true,
                  style: selectFilterReadOnlyInputStyle,
                }}
              />
            );
          }}
          sx={buildSingleSelectFilterAutocompleteSx({
            controlHeight,
            fontSizePx,
            hasActiveValue,
            inputBackgroundColor,
            inputLeftPadding,
          })}
          slotProps={{
            popper: {
              modifiers: buildSelectFilterPopperModifiers(),
              sx: buildSelectFilterPopperSx({ dropdownWidth, fullWidth }),
            },
            paper: {
              sx: buildFilterAutocompletePaperSx({ fullWidth, minWidth: dropdownWidth }),
            },
            listbox: {
              sx: buildSingleSelectFilterListboxSx({ dropdownWidth, optionRowMinHeight }),
            },
          }}
        />
      </Box>
    </Box>
  );
};

export type { SingleSelectFilterFieldProps, SingleSelectOption };
